import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, PlatformIcon, getPlatformColor, SelectorDropdown } from '@/components/primitives';
import { RankedTitleCard } from '@/components/organisms';
import { apiUrl } from '@/lib/api';
import styles from './Home.module.css';

interface LeaderboardEntry {
  parrot_id: string;
  title: string;
  content_type: string;
  availability_platform: string;
  revenue_contribution_usd: number;
  revenue_share: number;
  title_rank: number;
  main_genre?: string;
  release_year?: number;
}

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';

// Platforms to show in tabs (ordered by importance)
const PLATFORM_TABS = [
  'Netflix',
  'Amazon Prime Video', 
  'Disney+',
  'Max',
  'Paramount+',
  'Apple TV+',
  'Peacock',
  'Hulu',
];

async function fetchLeaderboard(params: {
  platform?: string;
  quarter?: string;
  region?: string;
  timePeriod?: string;
  limit?: number;
}): Promise<{ data: LeaderboardEntry[]; meta: { quarter: string } }> {
  const searchParams = new URLSearchParams();
  if (params.platform) searchParams.set('platform', params.platform);
  if (params.quarter) searchParams.set('quarter', params.quarter);
  if (params.region) searchParams.set('region', params.region);
  if (params.timePeriod) searchParams.set('timePeriod', params.timePeriod);
  searchParams.set('limit', String(params.limit || 10));

  const res = await fetch(apiUrl(`/leaderboard?${searchParams}`));
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
}

function formatRevenue(value: number): { amount: string; suffix: string } {
  if (value >= 1_000_000_000) {
    return { amount: (value / 1_000_000_000).toFixed(1), suffix: 'b' };
  }
  if (value >= 1_000_000) {
    return { amount: (value / 1_000_000).toFixed(0), suffix: 'm' };
  }
  return { amount: (value / 1_000).toFixed(0), suffix: 'k' };
}

export function Home() {
  const [selectedPlatform, setSelectedPlatform] = useState('Netflix');
  const [region, setRegion] = useState('');

  // All-time top 10 for selected platform
  const { data: allTimeData, isLoading: allTimeLoading } = useQuery({
    queryKey: ['alltime-top10', selectedPlatform, region],
    queryFn: () => fetchLeaderboard({ 
      platform: selectedPlatform, 
      region: region || undefined,
      timePeriod: 'all',
      limit: 10 
    }),
    staleTime: 5 * 60 * 1000,
  });

  // Latest quarter top 10 for each platform (for weekly section)
  const { data: weeklyData, isLoading: weeklyLoading } = useQuery({
    queryKey: ['weekly-top10', region],
    queryFn: async () => {
      const results: Record<string, LeaderboardEntry[]> = {};
      // Fetch top 5 for each platform in parallel
      await Promise.all(
        PLATFORM_TABS.slice(0, 6).map(async (platform) => {
          try {
            const res = await fetchLeaderboard({
              platform,
              region: region || undefined,
              limit: 5,
            });
            results[platform] = res.data;
          } catch {
            results[platform] = [];
          }
        })
      );
      return results;
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className={styles.page}>
      {/* Section 1: All-Time Revenue Champions */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderWrap}>
          <img
            src="/images/section-header-bg.png"
            alt=""
            className={styles.sectionBgImage}
          />
          <div className={styles.sectionHeaderContent}>
            <h1 className={styles.title}>Total Streaming Revenue</h1>
            <div className={styles.selectors}>
              <SelectorDropdown
                value={selectedPlatform}
                options={PLATFORM_TABS.map((p) => ({
                  value: p,
                  label: p.replace('Amazon Prime Video', 'Prime Video'),
                  icon: <PlatformIcon name={p} size={20} />,
                }))}
                onChange={(v) => setSelectedPlatform(v)}
                triggerIcon={<PlatformIcon name={selectedPlatform} size={20} />}
                headerLabel="Available Platforms"
                title="Select Platform"
              />
              <SelectorDropdown
                value={region}
                options={[
                  { value: '', label: 'WW' },
                  { value: 'UCAN', label: 'UCAN' },
                  { value: 'EMEA', label: 'EMEA' },
                  { value: 'APAC', label: 'APAC' },
                  { value: 'LATAM', label: 'LATAM' },
                ]}
                onChange={(v) => setRegion(v)}
                headerLabel="Region"
                title="Select Market"
              />
              <SelectorDropdown
                value="all"
                options={[
                  { value: 'all', label: 'All Time' },
                  { value: 'latest', label: 'Latest Quarter' },
                ]}
                onChange={() => {}}
                headerLabel="Time Period"
                title="Select Time Period"
              />
            </div>
          </div>
        </div>

        {/* All-Time Top 10 Grid */}
        {allTimeLoading && (
          <div className={styles.loading}>
            <Spinner size="lg" />
            <p>Loading top titles...</p>
          </div>
        )}

        {allTimeData && (
          <div className={styles.grid}>
            {allTimeData.data.map((entry, index) => (
              <RankedTitleCard
                key={entry.parrot_id}
                rank={index + 1}
                parrotId={entry.parrot_id}
                title={entry.title}
                releaseYear={entry.release_year}
                genre={entry.main_genre}
                revenueUsd={entry.revenue_contribution_usd}
              />
            ))}
          </div>
        )}

        {allTimeData?.data.length === 0 && (
          <div className={styles.empty}>
            <p>No titles found for {selectedPlatform}.</p>
          </div>
        )}
      </section>

      {/* Section 2: This Week's Top Performers */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <div>
              <h2 className={styles.title}>This Quarter's Top Performers</h2>
              <p className={styles.subtitle}>What's driving revenue right now across platforms</p>
            </div>
          </div>
        </header>

        {weeklyLoading && (
          <div className={styles.loading}>
            <Spinner size="lg" />
            <p>Loading weekly data...</p>
          </div>
        )}

        {weeklyData && (
          <div className={styles.weeklyGrid}>
            {PLATFORM_TABS.slice(0, 6).map((platform) => {
              const platformData = weeklyData[platform] || [];
              return (
                <div key={platform} className={styles.platformColumn}>
                  <div 
                    className={styles.platformColumnHeader}
                    style={{ borderTopColor: getPlatformColor(platform) }}
                  >
                    <PlatformIcon name={platform} size={24} />
                    <span>{platform.replace('Amazon Prime Video', 'Prime Video')}</span>
                  </div>
                  <div className={styles.platformColumnBody}>
                    {platformData.length === 0 && (
                      <div className={styles.noData}>No data</div>
                    )}
                    {platformData.map((entry, index) => (
                      <Link
                        key={entry.parrot_id}
                        to={`/titles/${entry.parrot_id}`}
                        className={styles.compactRow}
                      >
                        <span className={styles.compactRank}>{index + 1}</span>
                        <img
                          src={`${POSTER_CDN}/${entry.parrot_id}.jpg?width=100`}
                          alt={entry.title}
                          className={styles.compactPoster}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48"><rect fill="%23333" width="32" height="48"/></svg>';
                          }}
                        />
                        <span className={styles.compactTitle}>{entry.title}</span>
                        <span className={styles.compactRevenue}>
                          ${formatRevenue(entry.revenue_contribution_usd).amount}
                          {formatRevenue(entry.revenue_contribution_usd).suffix}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
